import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import { toast, ToastContainer } from "react-toastify";
import useEmployeeDetails from "../hooks/useEmployeeDetails";

export default function TeamLeaveCards() {
  const { id, employeeToken } = useEmployeeDetails();
  const API_URL = process.env.REACT_APP_BASE_URL;
  const [teamRequests, setTeamRequests] = useState([]);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);


  const fetchRequests =useCallback(async () => {
    try {
      const res = await axios.post(
        `${API_URL}/get_staff_leave_requests`,
        { reporting_manager: id, status: ["pending"] },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${employeeToken}`,
          },
        }
      );
      if (res.data?.employeeLeaveRequests) {
        setTeamRequests(res.data.employeeLeaveRequests);
      } else {
        toast.info("No leave requests found.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error fetching requests.");
    }
  },[id,API_URL,employeeToken])

  useEffect(() => {
     fetchRequests();
  }, [fetchRequests]);

  const getDuration = (start, end) => {
    const startDate = moment(start);
    const endDate = moment(end);
    return endDate.diff(startDate, "days") + 1;
  };

  const handleStatusChange = async (requestId, status) => {
    let reason = "";

    if (status === "rejected") {
      reason = window.prompt("Enter reason for rejection (optional):") || "";
    }

    try {
      const res = await axios.post(
        `${API_URL}/approve_reject_leave_request`,
        { requestId, status, reason },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${employeeToken}`,
          },
        }
      );

      if (res.data?.success) {
        toast.success(res.data.message || `Leave ${status} successfully.`);
        fetchRequests(); // Refresh list
      } else {
        toast.error(res.data.message || `Failed to ${status} leave.`);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to ${status} leave.`);
    }
  };


  return (
    <>
      <div className="px-3 max-w-2xl mx-auto">
        <ToastContainer />
        {teamRequests.length === 0 ? (
          <p className="text-gray-500 text-center">No leave requests yet.</p>
        ) : (
          <div className="space-y-2">
            {teamRequests.map((req) => {
              const emp = req.emp || {};
              const fullName = `${emp.first_name} ${emp.last_name}`;
              const createdAt = moment(req.created_at).format("DD MMM YY");
              const fromDate = moment(req.start_date).format("DD MMM");
              const toDate = moment(req.end_date).format("DD MMM YYYY");
              const duration = getDuration(req.start_date, req.end_date);

              return (
                <div
                  key={req._id}
                  className="bg-white shadow-sm rounded-xl border border-gray-200 space-y-1"
                >
                  <div className="p-2 space-y-0.5">
                    {/* Top: Name + Applied On */}
                    <div className="flex justify-between items-start">
                      <div className="text-sm font-semibold text-gray-800">
                        {fullName}
                      </div>
                      <div className="text-right text-[10px] text-gray-400 leading-tight">
                        <p className="font-light">Applied On</p>
                        <p>{createdAt}</p>
                      </div>
                    </div>

                    {/* Leave Type */}
                    {req.leave_type && (
                      <div className="border border-green-500 text-green-700 rounded-md inline-block text-xs px-2 py-0.5 font-medium">
                        {req.leave_type.replace(/_/g, " ").toLowerCase().replace(/(^\w|\s\w)/g, m => m.toUpperCase())}
                      </div>
                    )}

                    {/* Leave Date */}
                    <div className="text-xs text-gray-700 font-medium">
                      <span className="font-semibold text-gray-600">Leave Date: </span>
                      {fromDate} - {toDate}
                    </div>

                    {/* Duration */}
                    <div className="text-xs text-gray-600">
                      <span className="font-semibold text-gray-600">Duration&nbsp;&nbsp; &nbsp;: </span>
                      {duration} {duration > 1 ? "days" : "day"}
                    </div>

                    {/* Reason */}
                    {req.reason?.trim() && (
                      <div className="text-xs text-gray-700">
                        <span className="font-semibold">Reason &nbsp; &nbsp; &nbsp; : </span>
                        {req.reason}
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex w-full rounded-b-lg overflow-hidden text-center text-xs font-semibold">
                    <button
                      onClick={() => handleStatusChange(req._id, "approved")}
                      className="w-2/4 bg-[#D1EFEA] hover:bg-green-700 py-2 text-[#319582] hover:text-white uppercase"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => {
                        setSelectedRequestId(req._id);
                        setShowRejectModal(true);
                        setRejectionReason(""); // clear any previous input
                      }}
                      className="w-2/4 bg-[#FE8A8A33] hover:bg-red-600 py-2 text-[#FF3535] hover:text-white uppercase"
                    >
                      Reject
                    </button>

                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-80 shadow-xl space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">Reject Leave</h3>
            <p className="text-sm text-gray-600">Enter reason for rejection (optional):</p>
            <textarea
              rows="3"
              className="w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-green-500"
              placeholder="Optional reason..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedRequestId(null);
                  setRejectionReason("");
                }}
                className="px-4 py-1.5 text-sm rounded-md border border-gray-400 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    const res = await axios.post(
                      `${API_URL}/approve_reject_leave_request`,
                      { requestId: selectedRequestId, status: "rejected", reason: rejectionReason },
                      {
                        headers: {
                          "Content-Type": "application/json",
                          Authorization: `Bearer ${employeeToken}`,
                        },
                      }
                    );

                    if (res.data?.success) {
                      toast.success(res.data.message || `Leave rejected successfully.`);
                      fetchRequests();
                    } else {
                      toast.error(res.data.message || `Failed to reject leave.`);
                    }
                  } catch (error) {
                    toast.error(error.response?.data?.message || "Error rejecting leave.");
                  } finally {
                    setShowRejectModal(false);
                    setSelectedRequestId(null);
                    setRejectionReason("");
                  }
                }}
                className="px-4 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
